import { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ContextApp } from '../../context';
import { declination, getTranslateProps } from '../../utils';
import Spinner from '../spinner';
import ErrorIndicator from '../error-indicator';

const DescPage = ({ movie: { poster, age_category, runtime, director, actors, title, slogan, plot }, rating, additionalData: {releasedFormatted, endingMinutes, genre: { listGenres }, country: { listCountries }, listSimilarMovies, premiere, buttonFavorites } }) => {
    return (
        <section className='filmik'>
            <aside className='filmik__aside'>
                <div className='filmik__poster'>
                    <img src={poster} alt='poster' className='filmik__poster-image' />
                    {premiere}
                </div>
                {buttonFavorites}
                <ul className='filmik__aside-description'>
                    <li className='filmik__aside-description-item'>
                        <div className='filmik__aside-description-label'>Жанр</div>
                        <ul className='filmik__aside-description-genres'>
                            {listGenres}
                        </ul>
                    </li>
                    <li className='filmik__aside-description-item filmik__aside-description-item--country'>
                        <div className='filmik__aside-description-label'>Страна</div>
                        <ul className='filmik__aside-description-countries'>
                            {listCountries}
                        </ul>
                    </li>
                    <li className='filmik__aside-description-item'>
                        <div className='filmik__aside-description-label'>Мировая премьера</div>
                        <p className='filmik__aside-description-text'>{releasedFormatted}</p>
                    </li>
                    <li className='filmik__aside-description-item'>
                        <div className='filmik__aside-description-label'>Возрастная категория</div>
                        <p className='filmik__aside-description-text'>{age_category}+</p>
                    </li>
                    <li className='filmik__aside-description-item'>
                        <div className='filmik__aside-description-label'>Продолжительность</div>
                        <p className='filmik__aside-description-text'>{`${runtime} минут${endingMinutes}`}</p>
                    </li>
                    <li className='filmik__aside-description-item'>
                        <div className='filmik__aside-description-label'>Директор</div>
                        <p className='filmik__aside-description-text'>{director}</p>
                    </li>
                    <li className='filmik__aside-description-item'>
                        <div className='filmik__aside-description-label'>В главных ролях</div>
                        <p className='filmik__aside-description-text'>{actors}</p>
                    </li>
                </ul>
            </aside>
            <div className='filmik__content'>
                <article className='filmik__description'>
                    <div className='filmik__characteristics-top'>
                        <div className='filmik__characteristics-top-left'>
                            <div className='filmik__movie-name'>
                                {title}
                            </div>
                            <div className='filmik__characteristics-slogan'>
                                «{(slogan) ? slogan : 'Слоган отсутствует'}»
                            </div>
                        </div>
                        <div className='filmik__characteristics-rating'>{rating}</div>
                    </div>
                    <p className='filmik__characteristics-text'>{plot}</p>
                    {listSimilarMovies}
                </article>
            </div>
        </section>
    );
};

DescPage.propTypes = {
    movie: PropTypes.object.isRequired,
    rating: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.oneOf([undefined]).isRequired]),
    additionalData: PropTypes.object.isRequired,
};

const DescPageContainer = () => {
    const { state: { loading, movie, favoritesMoviesId, favoritesPremieresId, error }, actions: { movieRequested, movieLoaded, movieReset, movieAddedToFavorites, movieDeletedFromFavorites, premiereAddedToFavorites, premiereDeletedFromFavorites, movieError }, moviestoreService } = useContext(ContextApp);
    const [rating, setRating] = useState('');
    const [similarMovies, setSimilarMovies] = useState([]);

    const { id } = useParams();

    const preparedData = {
        endingsMinutes: ['а', 'ы', ''],
        endingsDays: ['ень', 'ня', 'ней'],
        formatter: new Intl.DateTimeFormat('ru', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        }),
    };

    const additionalData = {
        listSimilarMovies: null,
        releasedFormatted: '',
        countDay: null,
        premiere: null,
        endingMinutes: '',
        genre: {
            eng: [],
            rus: [],
            listGenres: null,
        },
        country: {
            eng: [],
            rus: [],
            listCountries: null,
        },
        buttonAddedToFavorites: null,
    };

    if (Object.keys(movie).length) {
        additionalData.releasedFormatted = preparedData.formatter.format(new Date(movie.released));
        additionalData.endingMinutes = declination(movie.runtime, preparedData.endingsMinutes);
        additionalData.countDay = Math.floor((new Date(movie.released) - new Date()) / 86400000);
        additionalData.premiere = ((new Date(movie.released) - new Date()) > 0) ? <div className='filmik__films-premiere'>До премьеры: {additionalData.countDay} д{declination(additionalData.countDay, preparedData.endingsDays)}</div> : null;

        movie.parameters[0].slice(6).split(',').forEach((genre) => {
            additionalData.genre.eng.push(genre);
            additionalData.genre.rus.push(getTranslateProps('genre', genre));
        });
        movie.parameters[1].slice(8).split(',').forEach((country) => {
            additionalData.country.eng.push(country);
            additionalData.country.rus.push(getTranslateProps('country', country));
        });

        additionalData.genre.listGenres = additionalData.genre.rus.map((genre, id) => {
            return (<li className='filmik__aside-description-genre' key={genre}>
                <Link className='filmik__aside-description-link' to={`/?type=${movie.type}&genre=${additionalData.genre.eng[id]}&page=1`}>
                    {genre}
                </Link>
            </li>);
        });

        additionalData.country.listCountries = additionalData.country.rus.map((country, id) => {
            return (<li className='filmik__aside-description-country' key={country}>
                <Link className='filmik__aside-description-link' to={`/?type=${movie.type}&country=${additionalData.country.eng[id]}&page=1`}>
                    {country}
                </Link>
            </li>);
        });

        additionalData.listSimilarMovies = (
            <>
                {
                    similarMovies.length ? <h2 className='filmik__similar-films-label'>{`Если вам понравился этот ${getTranslateProps('type', movie.type)}, ${(movie.similar_movies.length > 1) ? 'могут' : 'может'} понравиться:`}</h2> : null
                }
                <ul className='filmik__similar-films'>
                    {
                        similarMovies.map((item) => {
                            return (
                                <li className='filmik__similar-film' key={item.id}>
                                    <figure>
                                        <Link className='filmik__similar-film-link' to={`/desc/${item.id}`}>
                                            <img className='filmik__similar-film-image' src={item.similar_frame}
                                                 alt='similar 1' />
                                            <figcaption className='filmik__similar-film-name'>{item.title}</figcaption>
                                        </Link>
                                    </figure>
                                </li>
                            );
                        })
                    }
                </ul>
            </>
        );

        const getButtonFavorites = () => {
            let buttonFavorites = null;

            const currentMovieAddedToFavorites = (id) => {
                movieAddedToFavorites(Number(id));
            };

            const currentMovieDeletedFromFavorites = (id) => {
                movieDeletedFromFavorites(Number(id));
            };

            const currentPremiereAddedToFavorites = (id) => {
                premiereAddedToFavorites(Number(id));
            };

            const currentPremiereDeletedFromFavorites = (id) => {
                premiereDeletedFromFavorites(Number(id));
            };

            if (additionalData.premiere) {
                buttonFavorites = (!favoritesPremieresId.includes(Number(movie.id)))
                    ? <button className='filmik__btn-favorites' onClick={() => currentPremiereAddedToFavorites(movie.id)} type='button'>БУДУ СМОТРЕТЬ</button>
                    : <button className='filmik__btn-favorites' onClick={() => currentPremiereDeletedFromFavorites(movie.id)} type='button'>НЕ БУДУ СМОТРЕТЬ</button>;
            } else {
                buttonFavorites = (!favoritesMoviesId.includes(Number(movie.id)))
                    ? <button className='filmik__btn-favorites' onClick={() => currentMovieAddedToFavorites(movie.id)} type='button'>ДОБАВИТЬ В ИЗБРАННОЕ</button>
                    : <button className='filmik__btn-favorites' onClick={() => currentMovieDeletedFromFavorites(movie.id)} type='button'>УБРАТЬ ИЗ ИЗБРАННОГО</button>;
            }

            return buttonFavorites;
        };

        additionalData.buttonFavorites = getButtonFavorites();
    }

    useEffect(() => {
        movieRequested();
        moviestoreService.getMovieById(id)
            .then(({data}) => {
                movieLoaded(data.id);
            })
            .catch((err) => movieError(err));

        return () => {
            movieReset();
        };
        // eslint-disable-next-line
    }, [id]);

    useEffect(() => {
        if (Object.keys(movie).length) {
            moviestoreService.getImdbRating(movie.imdb_id)
                .then(({imdbRating}) => setRating(imdbRating))
                .catch((error) => console.log(error));

            moviestoreService.getPostersSimilarMovies(movie.similar_movies)
                .then((data) => setSimilarMovies(data))
                .catch((error) => console.log(error));
        }
        // eslint-disable-next-line
    }, [movie]);

    if (loading) {
        return <Spinner />
    }

    if (error) {
        return <ErrorIndicator error={error} />
    }

    return (
        <DescPage
            movie={movie}
            rating={rating}
            additionalData={additionalData}
        />
    );
};

export default DescPageContainer;